import { CommonModule, registerLocaleData } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import { NgModule } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSidenavModule } from '@angular/material/sidenav';
import { BrowserModule } from '@angular/platform-browser';
import { AutoRedemptionSettingsModule } from '@features/auto-redemption-settings/auto-redemption-settings.module';
import { EnrollmentModule } from '@features/enrollment/enrollment.module';
import { EnrollmentFacade } from '@features/enrollment/facades/enrollment.facade';
import { LoyaltyProgramModule } from '@features/loyalty-program/loyalty-program.module';
import { MembershipModule } from '@features/membership/membership.module';
import { OfferListingItemComponent } from '@features/offer-ui/components/offer-listing-item/offer-listing-item.component';
import { OfferHelper } from '@features/offer/helpers';
import { OfferModule } from '@features/offer/offer.module';
import { OfferSettingService } from '@features/offer/services/offer-setting.service';
import { PointsAccountModule } from '@features/points-account/points-account.module';
import { PointsSummaryModule } from '@features/points-summary/points-summary.module';
import { RewardsProgramModule } from '@features/rewards-program/rewards-program.module';
import { TransferConnectV2SettingService } from '@features/transfer-connect-v2/services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import { UserPreferenceHeaderInterceptor } from '@features/user-preference/interceptors/user-preference-header/user-preference-header.interceptor';
import {
  provideUnleashProxy,
  UnleashService,
} from '@karelics/angular-unleash-proxy-client';
import { LetModule, PushModule } from '@ngrx/component';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DayJsConfig } from '@rd-lib/dayjs.config';
import { RdLibModule } from '@rd-lib/rd-lib.module';
import { RdLibModuleHermesModeConfig } from '@rd-lib/rd-lib.module.config';
import { RD_UNLEASH_CONFIG } from '@rd-lib/unleash-configs';
import { BroadcastModule } from '@rd/broadcast/broadcast.module';
import { AuthFacade } from '@rd/core/auth/store/facades/auth.facade';
import { MainContainerComponent } from '@rd/core/containers/main-container/main-container.component';
import { NydusNetworkAuthHeaderInterceptor } from '@rd/core/interceptors/nydus-network-auth-header/nydus-network-auth-header.interceptor';
import { PageTitleService } from '@rd/core/services/page-title/page-title.service';
import { TransactionDetailsFormatterService } from '@rd/core/services/transaction-details-formatter/transaction-details-formatter.service';
import { EXTERNAL_LINK_ICON } from '@rd/core/tokens/link.token';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import {
  UBER_VOUCHER_ENABLED,
  UBER_VOUCHER_UNLEASH_KEY,
} from '@rd/core/tokens/unleash-flags.token';
import { PopupSettingService } from '@rd/popup/services/popup-setting/popup-setting.service';
import { RdAlertModule } from '@rd/shared-legacy/components/alert/alert.module';
import { BreadcrumbModule } from '@rd/shared-legacy/components/breadcrumb/breadcrumb.module';
import { ButtonModule } from '@rd/shared-legacy/components/button/button.module';
import { IconModule } from '@rd/shared-legacy/components/icon/icon.module';
import { LinkModule } from '@rd/shared-legacy/components/link/link.module';
import { ClickOutsideModule } from '@rd/shared-legacy/directives/click-outside/click-outside.module';
import { LocalizePointsNumberPipeModule } from '@rd/shared-legacy/pipes/localize-points-number/localize-points-number.pipe';
import { BannerImageSrcDirective } from '@rd/shared-legacy/widgets/banner-image/banner-image-src.directive';
import { BannerImageModule } from '@rd/shared-legacy/widgets/banner-image/banner-image.module';
import { UniqueSellingPointModule } from '@rd/shared-legacy/widgets/unique-selling-point/unique-selling-point.module';
import { IfLoggedInModule } from '@rd/shared/directives/if-logged-in/if-logged-in.directive';
import dayjs from 'dayjs';
import dayjsLocaleEnAU from 'dayjs/locale/en-au';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { MarkdownModule } from 'ngx-markdown';
import { combineLatest, map } from 'rxjs';
import { externalModules } from '../build-specifics';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { BnzAppComponent } from './app.component';
import { BnzBankWideHomepageBannerComponent } from './bank-wide/components/homepage-banner/homepage-banner.component';
import { BnzBankWideIntroductionDialogComponent } from './bank-wide/components/introduction-dialog/introduction-dialog.component';
import { BnzAuthSecureRouteDialogComponent } from './core/components/auth-secure-route-dialog/auth-secure-route-dialog.component';
import { BnzBypassBlockComponent } from './core/components/bypass-block/bypass-block.component';
import { BnzDesktopHeaderComponent } from './core/components/desktop-header/desktop-header.component';
import { BnzFooterComponent } from './core/components/footer/footer.component';
import { BnzMobileHeaderComponent } from './core/components/mobile-header/mobile-header.component';
import { BnzSideNavContentComponent } from './core/components/side-nav-content/side-nav-content.component';
import { MyAccountLinkDirective } from './core/directives/my-account-link/my-account-link.directive';
import { MyRedemptionLinkDirective } from './core/directives/my-redemption-link/my-redemption-link.directive';
import { BnzPageTitleService } from './core/services/bnz-page-title.service';
import { BnzPopupSettingService } from './core/services/popup-setting/popup-setting.service';
import {
  BNZ_AUTO_REDEMPTION_UPDATE_ENABLED,
  BNZ_AUTO_REDEMPTION_UPDATE_UNLEASH_KEY,
  BNZ_BANKWIDE_CAROUSEL_UPDATE_ENABLED,
  BNZ_BANKWIDE_LOGIN_COPY_UPDATE,
  BNZ_BANKWIDE_UPDATES_ENABLED,
  BNZ_BANKWIDE_UPDATES_UNLEASH_KEY,
  BNZ_BANK_WIDE_ENABLED,
  BNZ_CASHBACK_DESCRIPTOR_ENABLED,
  BNZ_DEPRECATE_FLYBUYS_SCHEME_ENABLED,
  BNZ_HOMEPAGE_BANNER_IMAGE_ENABLED,
  BNZ_HOMEPAGE_BANNER_IMAGE_UNLEASH_KEY,
  BNZ_OFFERS_BANNER_IMAGE_ENABLED,
  BNZ_POINTS_ACTIVITY_ENHANCE_ENABLED,
  BNZ_POINTS_ACTIVITY_ENHANCE_UNLEASH_KEY,
  BNZ_STAR_ICON_ENABLED,
  BNZ_USER_HAS_ENROLLMENTS,
  BNZ_VOX_ENABLED,
  BNZ_VOX_UNLEASH_KEY,
} from './core/tokens/features.tokens';
import { BnzOfferItemComponent } from './offer/components/offer-listing-item/offer-listing-item.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { BnzTransactionDetailsFormatterService } from './services/transaction-details-formatter.service';
import { BnzLinkModule } from './shared/components/link/link.module';
import { BnzFeaturedPartnerV2Component } from './static-pages/components/featured-partner/feature-partner-v2/featured-partner-v2.component';
import { BnzHomepageBannerImageComponent } from './static-pages/components/homepage-banner-image/homepage-banner-image.component';
import { BnzHomepageQuickLinkComponent } from './static-pages/components/homepage-quick-link/homepage-quick-link.component';
import { BnzHomePageComponent } from './static-pages/views/home-page/home-page.component';
import { reducers } from './store';
import { RootEffects } from './store/effects/root.effects';
import { BnzTransferConnectV2SettingService } from './transfer-connect-v2/services/transfer-connect-v2-setting.service';

registerLocaleData(localeEn, 'en-AU');
dayjs.extend(relativeTime, DayJsConfig.relativeTimeConfig);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.updateLocale('en-au', {
  ...dayjsLocaleEnAU,
  relativeTime: {
    ...dayjsLocaleEnAU.relativeTime,
    d: '1 business day',
    dd: '%d business days',
  },
});

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(
    http,
    `${environment.translationBaseUrl}/`,
    '.json',
  );
}

const declaredComponents: any[] = [
  BnzHomePageComponent,
  BnzHomepageQuickLinkComponent,
  BnzFooterComponent,
  BnzDesktopHeaderComponent,
  BnzMobileHeaderComponent,
  BnzSideNavContentComponent,
  BnzFooterComponent,
  PageNotFoundComponent,
  BnzBypassBlockComponent,
  BnzAppComponent,
];

const declaredDirectives = [MyRedemptionLinkDirective, MyAccountLinkDirective];

// INIT: Copied from bootstrap api
const supportedLocales: string[] = ['en-AU'];

@NgModule({
  declarations: [...declaredComponents, ...declaredDirectives],
  imports: [
    BannerImageModule,
    BannerImageSrcDirective,
    BnzFeaturedPartnerV2Component,
    BnzBankWideHomepageBannerComponent,
    BnzBankWideIntroductionDialogComponent,
    BnzHomepageBannerImageComponent,
    BnzOfferItemComponent,
    ClickOutsideModule,
    AutoRedemptionSettingsModule,
    PointsSummaryModule,
    PointsAccountModule,
    RewardsProgramModule,
    ButtonModule, // Old rd-lib button
    LinkModule, // Old rd-lib link
    BnzLinkModule,
    RdAlertModule,
    LocalizePointsNumberPipeModule,
    BreadcrumbModule,
    IconModule,
    MembershipModule,
    CommonModule,
    LoyaltyProgramModule,
    EnrollmentModule,
    BrowserModule,
    MatSidenavModule,
    BroadcastModule,
    OfferListingItemComponent,
    OfferModule,
    RdLibModule.forRoot({
      defaultCurrencyCode: 'NZD',
      defaultLocaleCode: 'en-AU',
      nydusNetworkApiBaseUrl: environment.nydusNetworkApiBaseUrl,
      env: environment.env,
      production: environment.production,
      guardhouseApiUrl: environment.guardhouseApiUrl,
      clientId: environment.clientId,
      connection: environment.connection,
      scope: environment.scope,
      redirectUri: environment.redirectUri,
      travelEdgeBaseUrl: environment.travelEdgeBaseUrl,
      complimentaryNightBaseUrl: null,
      brandName: environment.brandName,
      portalName: 'BNZ Rewards Portal',
      shortBrandName: environment.shortBrandName,
      tenantName: 'bnz',
      hermesMode: environment.hermesMode as RdLibModuleHermesModeConfig,
      idleCheckConfig: environment.idleCheckConfig,
      sessionCheckPollInterval: environment.sessionCheckPollInterval,
      cookiesConfig: environment.cookiesConfig,
      supportedLocales,
      cashNumberDigitsInfo: '1.2-2',
      pointsNumberDigitsInfo: '1.0-0',
      pointsTransactionDigitsInfo: '1.0-0',
      pointsTransactionEnabled: true,
      appId: environment.appId,
      prismicConfig: environment.prismicConfig,
      useCustomPageTitle: true,
      tenantTncsUrl: environment.redirectUri + '/terms_and_conditions',
      tenantPrivacyPolicyUrl: environment.redirectUri + '/privacy_policy',
    }),
    AppRoutingModule,
    UniqueSellingPointModule,
    MainContainerComponent,
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([RootEffects]),
    externalModules,
    LetModule,
    PushModule,
    IfLoggedInModule,
    MarkdownModule.forRoot(),
  ],
  providers: [
    OfferHelper,
    OfferSettingService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NydusNetworkAuthHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UserPreferenceHeaderInterceptor,
      multi: true,
    },
    { provide: PopupSettingService, useClass: BnzPopupSettingService },
    {
      provide: TransferConnectV2SettingService,
      useClass: BnzTransferConnectV2SettingService,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        hideRequiredMarker: true,
      },
    },
    {
      provide: CORE_AUTH_SECURE_ROUTE_DIALOG,
      useValue: BnzAuthSecureRouteDialogComponent,
    },
    {
      provide: EXTERNAL_LINK_ICON,
      useValue: 'icon-chevron-right',
    },
    {
      provide: PageTitleService,
      useClass: BnzPageTitleService,
    },
    {
      provide: TransactionDetailsFormatterService,
      useClass: BnzTransactionDetailsFormatterService,
    },
    provideUnleashProxy({
      ...RD_UNLEASH_CONFIG,
      appName: environment.unleashAppName,
      clientKey: environment.unleashClientKey,
    }),
    {
      provide: BNZ_BANK_WIDE_ENABLED,
      useValue: true,
    },
    {
      provide: BNZ_DEPRECATE_FLYBUYS_SCHEME_ENABLED,
      useValue: true,
    },
    {
      provide: BNZ_USER_HAS_ENROLLMENTS,
      useFactory: (
        authFacade: AuthFacade,
        enrollmentFacade: EnrollmentFacade,
      ) =>
        combineLatest([
          authFacade.isLoggedIn$,
          enrollmentFacade.enrollments$,
        ]).pipe(
          map(
            ([isLoggedIn, enrollments]) => isLoggedIn && !!enrollments?.length,
          ),
        ),
      deps: [AuthFacade, EnrollmentFacade],
    },
    {
      provide: BNZ_STAR_ICON_ENABLED,
      useValue: true,
    },
    {
      provide: BNZ_CASHBACK_DESCRIPTOR_ENABLED,
      useValue: true,
    },
    {
      provide: BNZ_POINTS_ACTIVITY_ENHANCE_ENABLED,
      useFactory: (unleashService: UnleashService) =>
        unleashService.isEnabled(BNZ_POINTS_ACTIVITY_ENHANCE_UNLEASH_KEY),
      deps: [UnleashService],
    },
    {
      provide: BNZ_AUTO_REDEMPTION_UPDATE_ENABLED,
      useFactory: (unleashService: UnleashService) =>
        unleashService.isEnabled(BNZ_AUTO_REDEMPTION_UPDATE_UNLEASH_KEY),
      deps: [UnleashService],
    },
    {
      provide: BNZ_HOMEPAGE_BANNER_IMAGE_ENABLED,
      useFactory: (unleashService: UnleashService) =>
        unleashService.isEnabled(BNZ_HOMEPAGE_BANNER_IMAGE_UNLEASH_KEY),
      deps: [UnleashService],
    },
    {
      provide: BNZ_OFFERS_BANNER_IMAGE_ENABLED,
      useValue: true,
    },
    {
      provide: BNZ_BANKWIDE_CAROUSEL_UPDATE_ENABLED,
      useValue: true,
    },
    {
      provide: BNZ_BANKWIDE_UPDATES_ENABLED,
      useFactory: (unleashService: UnleashService) =>
        unleashService.isEnabled(BNZ_BANKWIDE_UPDATES_UNLEASH_KEY),
      deps: [UnleashService],
    },
    {
      provide: BNZ_BANKWIDE_LOGIN_COPY_UPDATE,
      useFactory: (unleashService: UnleashService) =>
        unleashService.isEnabled('bnz_bankwide_login_copy_update'),
      deps: [UnleashService],
    },
    {
      provide: UBER_VOUCHER_ENABLED,
      useFactory: (unleashService: UnleashService) =>
        unleashService.isEnabled(UBER_VOUCHER_UNLEASH_KEY),
      deps: [UnleashService],
    },
    {
      provide: BNZ_VOX_ENABLED,
      useFactory: (unleashService: UnleashService) =>
        unleashService.isEnabled(BNZ_VOX_UNLEASH_KEY),
      deps: [UnleashService],
    },
  ],
  bootstrap: [BnzAppComponent],
})
export class AppModule {}
